import React, { useState } from 'react';
import Fuse from 'fuse.js';
import i18n from '@/utils/i18n';
import { Button, Input, InputGroup, InputGroupText, Tooltip } from 'reactstrap';
import { useEventsMinimalQuery } from '../../../graphql/queries/eventsMinimal.generated';
import { EventList, EventListProps } from './EventList';
import { getCurrentDatePlusDays } from '@/utils/date';
import { FaRegSave, FaSave } from 'react-icons/fa';

const STARTDATE_LOCALSTORAGE_KEY = 'orderfulfillment-event-startdate';
const ENDDATE_LOCALSTORAGE_KEY = 'orderfulfillment-event-enddate';
const ALL_EVENTS_LOCALSTORAGE_KEY = 'orderfulfillment-event-allevents';

const hasDateInLocalStorage = (key: string, stateDate: string | null) => {
  const dateLocalStorage = localStorage.getItem(key);

  return dateLocalStorage && dateLocalStorage === stateDate;
};

const getDateFromLocalStorage = (key: string, fallback: string | null) => {
  const dateLocalStorage = localStorage.getItem(key);

  if (dateLocalStorage) {
    return dateLocalStorage;
  }

  return fallback;
};

const getBooleanFromLocalStorage = (key: string) => {
  const boolLocalStorage = localStorage.getItem(key);

  if (boolLocalStorage === 'true') {
    return true;
  }

  return false;
};

type EventsListWithFilterProps = Omit<EventListProps, 'events'>;

export const EventsListWithFilter = (props: EventsListWithFilterProps) => {
  const [refresh, setRefresh] = useState(false);
  const startDateLocalStorage = getDateFromLocalStorage(
    STARTDATE_LOCALSTORAGE_KEY,
    getCurrentDatePlusDays(-14).toISOString().split('T')[0],
  );
  const endDateLocalStorage = getDateFromLocalStorage(
    ENDDATE_LOCALSTORAGE_KEY,
    null,
  );

  const allEventsLocalStorage = getBooleanFromLocalStorage(
    ALL_EVENTS_LOCALSTORAGE_KEY,
  );

  const [showStartDateTooltip, setShowStartDateTooltip] = useState(false);
  const [showEndDateTooltip, setShowEndDateTooltip] = useState(false);

  const [startDate, setStartDate] = useState<string | null>(
    startDateLocalStorage,
  );
  const [endDate, setEndDate] = useState<string | null>(endDateLocalStorage);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchAll, setSearchAll] = useState(allEventsLocalStorage);

  let variables = {};

  if (!searchAll) {
    // Server side date filter
    variables = {
      range: {
        from: startDate || '0000-01-01',
        to: endDate,
      },
    };
  }

  const {
    loading: loadingEvents,
    error: errorEvents,
    data: eventsData,
  } = useEventsMinimalQuery({
    variables: variables,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const hasStartDateInLocalStorage = hasDateInLocalStorage(
    STARTDATE_LOCALSTORAGE_KEY,
    startDate,
  );
  const hasEndDateInLocalStorage = hasDateInLocalStorage(
    ENDDATE_LOCALSTORAGE_KEY,
    endDate,
  );

  const hasSearchText = searchTerm.length > 0;

  const events = eventsData?.events;

  let eventsFiltered = events?.filter((e) => {
    if (searchAll && hasSearchText) return true;

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (!start && !end) return true;

    const eventStart = e.start_date ? new Date(e.start_date) : null;
    const eventEnd = e.end_date ? new Date(e.end_date) : null;

    if (!eventStart && !eventEnd) return false;

    if (eventStart && start && eventEnd && end) {
      return eventStart < end && eventEnd > start;
    }

    if (eventStart && end) {
      return eventStart < end;
    }

    if (eventEnd && start) {
      return eventEnd > start;
    }

    return true;
  });

  if (hasSearchText) {
    const eventsToFilter = searchAll ? events : eventsFiltered;

    const fuse = new Fuse(eventsToFilter || [], {
      keys: ['id', 'name'],
      threshold: 0.3,
    });

    const results = fuse.search(searchTerm);
    eventsFiltered = results.map((result) => result.item);
  }

  return (
    <div>
      <InputGroup className="mb-3">
        <InputGroupText>{i18n.t('Common:startDate')}</InputGroupText>
        <Input
          type="date"
          value={startDate || ''}
          onChange={(e) => {
            setStartDate(e.target.value);
            localStorage.removeItem(STARTDATE_LOCALSTORAGE_KEY);
          }}
        />

        <Button
          id="Tooltip-startDate"
          onClick={() => {
            if (!hasStartDateInLocalStorage) {
              localStorage.setItem(STARTDATE_LOCALSTORAGE_KEY, startDate || '');
            } else {
              localStorage.removeItem(STARTDATE_LOCALSTORAGE_KEY);
            }

            setRefresh(!refresh);
          }}
        >
          {!hasStartDateInLocalStorage && <FaRegSave />}
          {hasStartDateInLocalStorage && <FaSave />}
        </Button>

        <Tooltip
          placement="top"
          target="Tooltip-startDate"
          isOpen={showStartDateTooltip}
          toggle={() => {
            setShowStartDateTooltip(!showStartDateTooltip);
          }}
        >
          {!hasStartDateInLocalStorage && i18n.t('EventsList:datePin')}
          {hasStartDateInLocalStorage && i18n.t('EventsList:dateUnPin')}
        </Tooltip>
      </InputGroup>

      <InputGroup className="mb-3">
        <InputGroupText>{i18n.t('Common:endDate')}</InputGroupText>
        <Input
          type="date"
          value={endDate || ''}
          onChange={(e) => {
            setEndDate(e.target.value);
            localStorage.removeItem(ENDDATE_LOCALSTORAGE_KEY);
          }}
        />

        <Button
          id="Tooltip-endDate"
          onClick={() => {
            if (!hasEndDateInLocalStorage) {
              if (endDate === null) {
                return;
              }

              localStorage.setItem(ENDDATE_LOCALSTORAGE_KEY, endDate || '');
            } else {
              localStorage.removeItem(ENDDATE_LOCALSTORAGE_KEY);
            }

            setRefresh(!refresh);
          }}
        >
          {!hasEndDateInLocalStorage && <FaRegSave />}
          {hasEndDateInLocalStorage && <FaSave />}
        </Button>

        <Tooltip
          placement="top"
          target="Tooltip-endDate"
          isOpen={showEndDateTooltip}
          toggle={() => {
            setShowEndDateTooltip(!showEndDateTooltip);
          }}
        >
          {!hasEndDateInLocalStorage && i18n.t('EventsList:datePin')}
          {hasEndDateInLocalStorage && i18n.t('EventsList:dateUnPin')}
        </Tooltip>
      </InputGroup>

      <InputGroup>
        <InputGroupText>{i18n.t('Common:search')}</InputGroupText>
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.currentTarget.value);
          }}
        />
        <InputGroupText>
          <Input
            type="checkbox"
            checked={searchAll}
            onChange={() => {
              const newState = !searchAll;

              setSearchAll(newState);
              localStorage.setItem(
                ALL_EVENTS_LOCALSTORAGE_KEY,
                newState.toString(),
              );
            }}
          />
          &nbsp;{i18n.t('Common:all')}
        </InputGroupText>
      </InputGroup>

      <br />

      {loadingEvents && <div>{i18n.t('EventsList:loading')}</div>}
      {errorEvents && <div>{i18n.t('EventsList:error')}</div>}
      {(eventsFiltered == null || eventsFiltered.length) === 0 && (
        <div>{i18n.t('EventsList:noEvents')}</div>
      )}
      {eventsFiltered != null && eventsFiltered.length > 0 && (
        <EventList
          events={eventsFiltered}
          showSelect={props.showSelect}
          showDeposit={false}
          setEventId={props.setEventId}
        />
      )}
    </div>
  );
};
